/**
 * Which programmes an account may manage the team of.
 *
 * Pure, so the permission rule is testable without a screen, and so the routes
 * showing it cannot disagree about the list.
 *
 * @module components/teams/manageable-programmes
 */

import { compareText } from "@/components/teams/team-assignments";
import type { ProgrammeSummary, SessionContext } from "@/platform";
import { can } from "@/server/authorization";

/**
 * @param session - Whose permissions decide the list.
 * @param programmes - Every programme the account can reach.
 * @param allowLeadTeamManagement - The tenant setting. An admin is exempt: it
 *   governs whether a lead may manage their own team, not whether an
 *   administrator may.
 */
export function manageableProgrammes(
  session: SessionContext,
  programmes: readonly ProgrammeSummary[],
  allowLeadTeamManagement: boolean,
): ProgrammeSummary[] {
  const isTenantAdmin =
    session.subject.accountRole === "client-admin" ||
    session.subject.accountRole === "platform-admin";
  return programmes
    .filter((programme) =>
      can(session.subject, "assignment:manage", {
        type: "programme",
        programmeId: programme.id,
      }),
    )
    .filter(() => isTenantAdmin || allowLeadTeamManagement)
    .sort((left, right) => compareText(left.title, right.title));
}
